import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { admin_cse, provider_case_manager, provider_peer } from "@/config/roles";
import { Button2 } from "@/ui2/atoms/Button";
import { canCreateAdvancedCareActivityNotes } from "@/features/acp/helpers/note-creation";
import ClipboardIcon from "@/assets/images/icons2/icon--clipboard.svg?component";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { isAuthorized } from "@/redux/actions/auth";
import { Modal2 } from "@/ui2/molecules/Modal";

import { NewNoteItem } from "./components/NewNoteItem";

export function NewNoteModal({
  isAuthorizedToCreate,
  patientDetails,
  patientDiagnosis,
  sessionCount,
}) {
  const history = useHistory();
  const providerType = useSelector((state) => state.auth.profile?.provider_type);
  const { patientId } = useParams();

  const allowedRolesByNoteTitle = {
    "Advanced Care Activity": canCreateAdvancedCareActivityNotes(providerType),
    "Care Communication Note":
      !isAuthorized(admin_cse) &&
      !isAuthorized(provider_peer) &&
      !isAuthorized(provider_case_manager),
    "Care Management Assessment":
      (isAuthorized("provider:behavioral") && providerType === "BEHAVIORAL") ||
      isAuthorized(provider_case_manager),
    "Case Management Note":
      !isAuthorized(admin_cse) &&
      !isAuthorized(provider_peer) &&
      !isAuthorized(provider_case_manager),
    "Guided Session Note":
      !isAuthorized(admin_cse) &&
      !isAuthorized(provider_peer) &&
      !isAuthorized(provider_case_manager),
  };

  return (
    <Modal2
      data-public
      overlayProps={{ isDismissable: true }}
      title="Create New Note"
      trigger={
        <Button2
          icon={<ClipboardIcon />}
          isDisabled={!isAuthorizedToCreate}
          {...getAutomationDataAttr("MemberProfileNotes_addNewNote")}
        >
          New Note
        </Button2>
      }
    >
      <ul className="tw:flex tw:flex-col tw:gap-4">
        {allowedRolesByNoteTitle["Guided Session Note"] && (
          <NewNoteItem
            iconPath="./icons/note-icon.svg"
            onClick={() => {
              let url = `/app/patient/${patientId}/note/add/v2?numSessions=${sessionCount}`;

              history.push(url);
            }}
            subTitle="Create a guided session note manually."
            title="Guided Session Note"
          />
        )}

        {allowedRolesByNoteTitle["Care Communication Note"] && (
          <NewNoteItem
            iconPath="./icons/care-note-icon.svg"
            onClick={() => {
              history.push(`/app/patient/${patientId}/care_communication`);
            }}
            subTitle="Record communication regarding a patient’s care outside of a Guided Session."
            title="Care Communication Note"
          />
        )}

        {allowedRolesByNoteTitle["Case Management Note"] && (
          <NewNoteItem
            iconPath="./icons/cm-note.jpg"
            iconStyle={{ height: 36 }}
            onClick={() => {
              history.push(`/app/patient/${patientId}/case_management`);
            }}
            subTitle="For case management assessment and service plan."
            title="Case Management Note"
          />
        )}

        {allowedRolesByNoteTitle["Care Management Assessment"] && (
          <NewNoteItem
            iconPath="./icons/cm-note.jpg"
            iconStyle={{ height: 36 }}
            onClick={() => {
              history.push(`/app/patient/${patientId}/care_management`, {
                patientDetails,
                patientDiagnosis,
              });
            }}
            title="Care Management Assessment"
          />
        )}

        {allowedRolesByNoteTitle["Advanced Care Activity"] && (
          <NewNoteItem
            iconPath="./icons/cm-note.jpg"
            iconStyle={{ height: 36 }}
            onClick={() => {
              history.push(`/app/patient/${patientId}/advanced_care_activity`);
            }}
            subTitle="Complete for any care manager or peer activity regardless of eligibility for advanced care."
            title="Advanced Care Activity"
          />
        )}
      </ul>
    </Modal2>
  );
}
